import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { DRAWER_DATA } from '../../../shared/injector-tokens';
import { BucketService } from '../../../services/bucket.service';
import { RecurringTransactionDrawerData } from '../../../services/drawer.service';
import {
  RecurringTransactionCadence,
  RecurringTransactionDetail,
  RecurringMatchedTransaction,
  RecurringTransactionService,
} from '../../../services/recurring-transaction.service';
import { Transaction } from '../../../types';
import { TransactionListItemComponent } from '../../../transactions/transaction-list-item/transaction-list-item.component';
import { DrawerLayoutComponent } from '../../drawer-layout/drawer-layout.component';
import { DrawerSectionComponent } from '../../drawer-layout/drawer-section.component';
import { MerchantAvatarComponent } from '../../../shared/merchant-avatar/merchant-avatar.component';

@Component({
  selector: 'app-recurring-transaction-drawer-wrapper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './recurring-transaction-drawer-wrapper.component.html',
  imports: [
    CurrencyPipe,
    DatePipe,
    MerchantAvatarComponent,
    DrawerLayoutComponent,
    DrawerSectionComponent,
    TransactionListItemComponent,
  ],
})
export class RecurringTransactionDrawerWrapperComponent {
  readonly data = inject<RecurringTransactionDrawerData>(DRAWER_DATA);
  private readonly recurringTransactionService = inject(RecurringTransactionService);

  private readonly bucketService = inject(BucketService);

  readonly detailQuery = injectQuery(() =>
    this.recurringTransactionService.detailQueryOptions(this.data.recurringTransactionId),
  );
  readonly detail = computed(() => this.detailQuery.data() ?? null);
  readonly bucketIdentitiesQuery = injectQuery(() => this.bucketService.allBucketsQueryOptions());
  readonly bucketIdentities = computed(() => this.bucketIdentitiesQuery.data()?.data ?? []);

  amount(detail: RecurringTransactionDetail): number | null {
    return detail.amountSummary.expectedAmount ?? detail.amountSummary.averageAmount ?? detail.amountSummary.lastAmount;
  }

  totalMatched(detail: RecurringTransactionDetail): number {
    return detail.matchedTransactions.reduce((total, transaction) => total + Math.abs(transaction.amount), 0);
  }

  matchedTransactionsNewestFirst(detail: RecurringTransactionDetail): RecurringMatchedTransaction[] {
    return [...detail.matchedTransactions].sort((left, right) => right.date.localeCompare(left.date));
  }

  matchedTransaction(transaction: RecurringMatchedTransaction): Transaction {
    return {
      id: transaction.id,
      amount: transaction.amount,
      netValue: transaction.netValue,
      merchant: transaction.merchant,
      date: transaction.date,
      description: transaction.description ?? undefined,
      bucketId: transaction.bucketId ?? '',
      createdByUserId: transaction.createdByUserId ?? '',
      children: [],
      deleted: transaction.deleted ?? false,
      labelIds: transaction.labelIds ?? [],
      type: transaction.type ?? 'TRANSACTION',
      transferBucket: { id: '', name: '' },
      _objectType: 'transaction',
      suggestedBucketIds: transaction.suggestedBucketIds ?? undefined,
      locked: transaction.locked
        ? { value: transaction.locked.value, reason: transaction.locked.reason ?? '' }
        : undefined,
      bankMeta: transaction.bankMeta ?? undefined,
      merchantMeta: transaction.merchantMeta,
      plaidAccount: transaction.plaidAccount
        ? {
            name: transaction.plaidAccount.name,
            nickname: transaction.plaidAccount.nickname ?? undefined,
            officialName: transaction.plaidAccount.officialName ?? undefined,
            mask: transaction.plaidAccount.mask ?? undefined,
            institution: transaction.plaidAccount.institution ?? undefined,
          }
        : undefined,
    };
  }

  cadenceLabel(cadence: RecurringTransactionCadence): string {
    switch (cadence) {
      case 'WEEKLY':
        return 'Weekly';
      case 'EVERY_TWO_WEEKS':
        return 'Every two weeks';
      case 'SEMI_MONTHLY':
        return 'Twice a month';
      case 'MONTHLY':
        return 'Monthly';
      case 'EVERY_TWO_MONTHS':
        return 'Every two months';
      case 'EVERY_SIX_MONTHS':
        return 'Every six months';
      case 'YEARLY':
        return 'Yearly';
    }
  }

  amountBehaviorLabel(detail: RecurringTransactionDetail): string {
    return detail.amountBehavior === 'FIXED' ? 'Fixed' : 'Varies';
  }

  paymentSourceLabels(detail: RecurringTransactionDetail): string[] {
    return [
      ...new Set(
        (detail.paymentSourceEvidence ?? []).flatMap(source => {
          if (!source.name && !source.mask) return [];
          if (source.name && source.mask) return [`${source.name} ••${source.mask}`];
          if (source.mask) return [`••${source.mask}`];
          return source.name ? [source.name] : [];
        }),
      ),
    ];
  }
}
